import React, { useState, useEffect } from 'react';
import { usePageLoaderContext } from '../../../contexts/PageLoaderContext';
import { useNotification } from '../../../contexts/NotificationContext';
import apiService from '../../../services/api';
import TreeDiagram from './components/TreeDiagram';
import SkeletonLoader from '../../UI/SkeletonLoader';
import styles from '../../../styles/CampusHierarchy.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';

interface HierarchyUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  employee_id: string;
  mobile_number: string;
  is_main_admin: boolean;
  designation?: string | null;
  supervisor: {
    id: string;
    full_name: string;
    designation: string;
  } | null;
  subordinates_count: number;
}

interface HierarchyLevel {
  designation: string;
  designation_display: string;
  level: number;
  is_single_account: boolean;
  users: HierarchyUser[];
}

interface StructureValidation {
  is_valid: boolean;
  has_principal: boolean;
  topmost_level: string | null;
  missing_levels: string[];
  warnings: string[];
}

const CampusHierarchy: React.FC = () => {
  const { showLoader, hideLoader } = usePageLoaderContext();
  const notificationService = useNotification();
  const [hierarchyData, setHierarchyData] = useState<HierarchyLevel[]>([]);
  const [structureValidation, setStructureValidation] = useState<StructureValidation | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoadingHierarchy, setIsLoadingHierarchy] = useState(false);

  const loadHierarchy = async () => {
    // Prevent multiple simultaneous calls
    if (isLoadingHierarchy) {
      return;
    }

    setIsLoadingHierarchy(true);
    setLoading(true);
    showLoader('Loading campus hierarchy...');

    try {
      const response = await apiService.getCampusHierarchy();

      if (response.status === 'success') {
        setHierarchyData(response.data || []);
        setStructureValidation((response as any).structure_validation || null);
      } else {
        notificationService.error(response.message || 'Failed to load hierarchy');
      }
    } catch (err: any) {
      console.error('Error loading hierarchy:', err);
      notificationService.error(err.response?.data?.message || 'Failed to load campus hierarchy. Please try again.');
    } finally {
      setLoading(false);
      setIsLoadingHierarchy(false);
      hideLoader();
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadHierarchy();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, []);

  const formatDesignation = (designation: string) => {
    return designation.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className={styles.campusHierarchyContainer}>
      {/* Content Section */}
      <div className={styles.contentSection}>
        {/* Structure Validation Warnings */}
        {structureValidation && (!structureValidation.is_valid || structureValidation.warnings.length > 0) && (
          <div className={styles.validationBanner}>
            <div className={styles.validationHeader}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <h3>Structure Validation</h3>
            </div>
            <ul className={styles.validationList}>
              {structureValidation.warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
              {structureValidation.missing_levels.length > 0 && (
                <li>
                  Missing levels: {structureValidation.missing_levels.map(d => formatDesignation(d)).join(', ')}
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Tree Diagram */}
        <div className={styles.treeWrapper}>
          {loading ? (
            <div className={styles.treeSkeletonContainer}>
              <div className={styles.treeSkeleton}>
                {/* Logo skeleton */}
                <div className={styles.skeletonLogo}></div>
                {/* Dashed connection */}
                <div className={styles.skeletonDashedLine}></div>
                {/* Top level node */}
                <div className={styles.skeletonNodeRow}>
                  <div className={styles.skeletonCard}>
                    <div className={styles.skeletonCardAccent}></div>
                    <div className={styles.skeletonCardContent}>
                      <div className={styles.skeletonAvatar}></div>
                      <div className={styles.skeletonLines}>
                        <div className={styles.skeletonLine}></div>
                        <div className={styles.skeletonLineShort}></div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Connection with dot */}
                <div className={styles.skeletonConnectorGroup}>
                  <div className={styles.skeletonConnectorLine}></div>
                  <div className={styles.skeletonDot}></div>
                </div>
                {/* Second level nodes */}
                <div className={styles.skeletonNodeRow}>
                  <div className={styles.skeletonCard}>
                    <div className={styles.skeletonCardAccent}></div>
                    <div className={styles.skeletonCardContent}>
                      <div className={styles.skeletonAvatar}></div>
                      <div className={styles.skeletonLines}>
                        <div className={styles.skeletonLine}></div>
                        <div className={styles.skeletonLineShort}></div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.skeletonCard}>
                    <div className={styles.skeletonCardAccent}></div>
                    <div className={styles.skeletonCardContent}>
                      <div className={styles.skeletonAvatar}></div>
                      <div className={styles.skeletonLines}>
                        <div className={styles.skeletonLine}></div>
                        <div className={styles.skeletonLineShort}></div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Connections with dots */}
                <div className={styles.skeletonConnectorsRow}>
                  <div className={styles.skeletonConnectorGroup}>
                    <div className={styles.skeletonConnectorLine}></div>
                    <div className={styles.skeletonDot}></div>
                  </div>
                  <div className={styles.skeletonConnectorGroup}>
                    <div className={styles.skeletonConnectorLine}></div>
                    <div className={styles.skeletonDot}></div>
                  </div>
                </div>
                {/* Third level nodes */}
                <div className={styles.skeletonNodeRow}>
                  <div className={styles.skeletonCard}>
                    <div className={styles.skeletonCardAccent}></div>
                    <div className={styles.skeletonCardContent}>
                      <div className={styles.skeletonAvatar}></div>
                      <div className={styles.skeletonLines}>
                        <div className={styles.skeletonLine}></div>
                        <div className={styles.skeletonLineShort}></div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.skeletonCard}>
                    <div className={styles.skeletonCardAccent}></div>
                    <div className={styles.skeletonCardContent}>
                      <div className={styles.skeletonAvatar}></div>
                      <div className={styles.skeletonLines}>
                        <div className={styles.skeletonLine}></div>
                        <div className={styles.skeletonLineShort}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <TreeDiagram hierarchyData={hierarchyData} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CampusHierarchy;

